/**
 * Mailversand je Generierung auf der Log-Seite (KW-023, docket P-4170).
 *
 * Füllt den Slot `LogInvoicePageProps.mailSpalte` aus invoice-frontend (≥ 3.68.0): eine Spalte mit den Zählern
 * „gesendet / fehlgeschlagen / gesperrt“ und, in der aufgeklappten Zeile, die Liste der nicht zugestellten Adressen
 * mit dem Grund. Gelesen wird `generationEmailResults.byGenerationLog` (saas-invoice-api-backend ≥ 3.18.0) — je Zeile
 * eine Abfrage mit genau einer Log-Id (ENTSCHEID observer 14783, E4), nur für Zeilen vom Typ `generation`.
 *
 * - Antwort `null` (keine Mails verlangt) ⇒ „—“.
 * - Leseweg wirft ⇒ „—“ mit Hinweis im `title`; kein Toast je Zeile (E7), daher `suppressGlobalErrorToast`.
 * - Fehlercodes `absender_fehlt` und `suppressed` werden übersetzt, jeder andere Text bleibt der Text des
 *   Anbieters (E6). Adresse und Grund sind nach 30 Tagen gelöscht (E2) und erscheinen dann als „—“.
 */

import type {
  InvoiceLogEntry,
  LogMailSpalte,
} from '@xhubio-saas/invoice-frontend/plugin-log-invoice'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc'

const LEER = '—'

/** Codes, die der Leseweg statt eines Anbietertexts liefert — sie haben einen Satz im Katalog. */
const UEBERSETZTE_FEHLER = new Set(['absender_fehlt', 'suppressed'])

function useMailErgebnis(generationLogId: string) {
  return trpc.generationEmailResults.byGenerationLog.useQuery(
    { generationLogId },
    // E7: eine Absage je Zeile wäre eine Toast-Flut — die Zelle zeigt den Hinweis selbst.
    { meta: { suppressGlobalErrorToast: true } }
  )
}

function MailZelle({ generationLogId }: { generationLogId: string }) {
  const { t } = useTranslation()
  const { data, error, isLoading } = useMailErgebnis(generationLogId)

  if (isLoading) return <span data-testid="log-mail-zelle" className="text-muted-foreground" />
  if (error) {
    return (
      <span
        data-testid="log-mail-zelle"
        className="text-muted-foreground"
        title={t('logInvoiceMail.nichtGeladen')}
      >
        {LEER}
      </span>
    )
  }
  if (!data) {
    return (
      <span data-testid="log-mail-zelle" className="text-muted-foreground">
        {LEER}
      </span>
    )
  }

  const { sent, failed, suppressed } = data.counts
  return (
    <span
      data-testid="log-mail-zelle"
      className="inline-flex flex-wrap items-center gap-x-2 text-sm"
    >
      <span className="text-muted-foreground">{t('logInvoiceMail.praefix')}</span>
      <span>{t('logInvoiceMail.gesendet', { anzahl: sent })}</span>
      {failed > 0 && (
        <span data-testid="log-mail-fehlgeschlagen" className="text-destructive">
          {t('logInvoiceMail.fehlgeschlagen', { anzahl: failed })}
        </span>
      )}
      {suppressed > 0 && (
        <span data-testid="log-mail-gesperrt" className="text-amber-600 dark:text-amber-400">
          {t('logInvoiceMail.gesperrt', { anzahl: suppressed })}
        </span>
      )}
    </span>
  )
}

function MailDetail({ generationLogId }: { generationLogId: string }) {
  const { t } = useTranslation()
  const { data, error } = useMailErgebnis(generationLogId)

  if (error) {
    return (
      <p data-testid="log-mail-detail" className="text-sm text-muted-foreground">
        {t('logInvoiceMail.nichtGeladen')}
      </p>
    )
  }
  if (!data) return null

  // Schlüssel ohne Index: nach 30 Tagen sind Adresse und Grund gelöscht (E2), gleiche Zeilen werden durchgezählt.
  const gezaehlt = new Map<string, number>()
  const zeilen = data.failures.map((f) => {
    const basis = `${f.createdAt}|${f.status}|${f.email ?? ''}`
    const n = (gezaehlt.get(basis) ?? 0) + 1
    gezaehlt.set(basis, n)
    return { f, key: `${basis}|${n}` }
  })

  return (
    <div data-testid="log-mail-detail" className="space-y-2">
      <h4 className="text-sm font-medium">{t('logInvoiceMail.detailTitel')}</h4>
      {data.failures.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('logInvoiceMail.keineFehlschlaege')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-4 font-normal">{t('logInvoiceMail.adresse')}</th>
              <th className="py-1 pr-4 font-normal">{t('logInvoiceMail.status')}</th>
              <th className="py-1 font-normal">{t('logInvoiceMail.grund')}</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map(({ f, key }) => (
              <tr key={key} className="border-t">
                <td className="py-1 pr-4 break-all">{f.email ?? LEER}</td>
                <td className="py-1 pr-4">
                  {f.status === 'suppressed'
                    ? t('logInvoiceMail.statusGesperrt')
                    : t('logInvoiceMail.statusFehlgeschlagen')}
                </td>
                <td data-testid="log-mail-fehler" className="py-1">
                  {f.error === null
                    ? LEER
                    : UEBERSETZTE_FEHLER.has(f.error)
                      ? t(`logInvoiceMail.fehler.${f.error}`)
                      : f.error}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Der Slot für `LogInvoicePage` — Spalte und Detail lesen nur Zeilen vom Typ `generation`. */
export function useLogMailSpalte(): LogMailSpalte {
  const { t } = useTranslation()
  const header = t('logInvoiceMail.header')
  return useMemo(
    () => ({
      header,
      cell: (entry: InvoiceLogEntry) =>
        entry.type === 'generation' ? <MailZelle generationLogId={entry.id} /> : null,
      detail: (entry: InvoiceLogEntry) =>
        entry.type === 'generation' ? <MailDetail generationLogId={entry.id} /> : null,
    }),
    [header]
  )
}
